import React, { useContext, useState, useEffect } from "react";
import { Form as FinalForm, Field } from "react-final-form";
import { Container, Form, Button, Dropdown, Label } from "semantic-ui-react";
import {
  combineValidators,
  composeValidators,
  isRequired,
  hasLengthLessThan,
  isNumeric,
  hasLengthGreaterThan,
  matchesPattern,
} from "revalidate";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";
import { RootStoreContext } from "../../../../app/stores/rootStore";
import TextInput from "../../../../app/common/form/TextInput";
import LoadingComponent from "../../../../app/layout/LoadingComponent";
import SelectInput from "../../../../app/common/form/SelectInput";

const validate = combineValidators({
  companyID: composeValidators(
    isRequired({ message: "El NIT es obligatorio" }),
    isNumeric({ message: "El NIT debe ser numerico" }),
    hasLengthGreaterThan(6)({
      message: "El NIT debe tener al menos 7 caracteres",
    }),
    hasLengthLessThan(11)({
      message: "El NIT debe tener máximo 10 caracteres",
    })
  )(),
  digitCheck: composeValidators(
    isRequired({ message: "El DV es obligatorio" }),
    isNumeric({ message: "El DV debe ser numerico" }),
    hasLengthLessThan(2)({
      message: "El DV debe tener máximo 1 caracter",
    })
  )(),
  registrationName: composeValidators(
    isRequired({ message: "El Nombre o Razón Social es obligatorio" }),
    hasLengthGreaterThan(1)({
      message: "El Nombre o Razón Social debe tener al menos 2 caracteres",
    }),
    hasLengthLessThan(257)({
      message: "El Nombre o Razón Social debe tener máximo 256 caracteres",
    }),
    matchesPattern(/^[A-zÀ-ÿ0-9 '.,;:&_@\-]*$/)({
      message:
        "Nombre no válido. Contiene caracteres especiales diferente a: ' . , ; : & _ @",
    })
  )(),
  name: composeValidators(
    isRequired({ message: "El Nombre Comercial es obligatorio" }),
    hasLengthGreaterThan(1)({
      message: "El Nombre Comercial debe tener al menos 2 caracteres",
    }),
    hasLengthLessThan(257)({
      message: "El Nombre Comercial debe tener máximo 256 caracteres",
    }),
    matchesPattern(/^[A-zÀ-ÿ0-9 '.,;:&_@\-]*$/)({
      message:
        "Nombre Comercial no válido. Contiene caracteres especiales diferente a: ' . , ; : & _ @",
    })
  )(),
  "contact.phone": composeValidators(
    isRequired({ message: "El Teléfono es obligatorio" }),
    matchesPattern(/^[a-zA-Z0-9\.()-\\]*$/)({
      message:
        "Teléfono no válido. Contiene espacios o caracteres especiales diferente a: ( ) - . \\",
    }),
    hasLengthGreaterThan(6)({
      message: "El Teléfono debe tener al menos 7 caracteres",
    }),
    hasLengthLessThan(33)({
      message: "El Teléfono debe tener máximo 32 caracteres",
    })
  )(),
  "address.line": composeValidators(
    isRequired({ message: "La Dirección es obligatoria" }),
    hasLengthGreaterThan(6)({
      message: "La Dirección debe tener al menos 7 caracteres",
    }),
    hasLengthLessThan(257)({
      message: "La Dirección debe tener máximo 256 caracteres",
    }),
    matchesPattern(/^[a-zA-Z0-9,_#-\s\\.]*$/)({
      message:
        "Direccion no válida. Contiene caracteres especiales diferente a: # , - _ . \\",
    }),
  )(),
  "address.postalZone": composeValidators(
    hasLengthLessThan(7)({
      message: "El Código postal debe tener máximo 6 caracteres",
    }),
    hasLengthGreaterThan(5)({
      message: "El Código postal debe tener al menos 6 caracteres",
    }),
    isNumeric({ message: "El Código postal debe ser numerico" }),
  )(),
  "address.countrySubentityCode": composeValidators(
    isRequired({ message: "Debe seleccionar un Departamento" })
  )(),
  "address.cityCode": composeValidators(
    isRequired({ message: "Debe seleccionar una Ciudad" })
  )(),
  "contact.email": composeValidators(
    isRequired({ message: "El Email es obligatorio" }),
    matchesPattern(/^([\w\.\-]+)@([\w\-]+)((\.(\w){2,3})+)$/)({
      message: "Por favor digite un Email valido",
    }),
    hasLengthLessThan(257)({
      message: "El Email debe tener máximo 256 caracteres",
    }),
    hasLengthGreaterThan(5)({
      message: "El Email debe tener al menos 6 caracteres",
    }),
  )(),
});

interface IProps {
  id: string;
}

const EditBrokerForm: React.FC<IProps> = ({ id }) => {
  //variables para uso la informacion del userStore
  const rootStore = useContext(RootStoreContext);
  const {
    getCities,
    cities,
    getCountrySubentities,
    countrySubentities,
  } = rootStore.listOfValours;
  const {
    updateBroker,
    btEditBroker,
    broker,
    loadBroker,
    newBroker,
  } = rootStore.brokerStore;
  const { getSellersOption, sellersOption } = rootStore.sellerStore;

  useEffect(() => {
    getCountrySubentities();
    if (id !== "null") {
      try {
        if (broker?.address.countrySubentityCode != null) {
          getCities(broker?.address.countrySubentityCode);
        }
      } catch {}
    }
    getSellersOption();
  }, [getCountrySubentities, getCities, broker, id, getSellersOption]);

  const [companyForm] = useState(
    id === "null"
      ? {
          companyID: "",
          digitCheck: "",
          registrationName: "",
          sellerId: "",
          contact: { email: "", phone: "" },
          name: "",
          address: {
            postalZone: "",
            countrySubentityCode: "",
            countrySubentityName: "",
            cityCode: "",
            countryIdentificationCode: "",
            cityName: "",
            countryName: "",
            languaje: "",
            line: "",
          },
        }
      : {
          id: broker?.id,
          companyID: broker?.companyID,
          digitCheck: broker?.digitCheck,
          sellerId: broker?.sellerId,
          registrationName: broker?.registrationName,
          contact: broker?.contact || { email: "", phone: "" },
          name: broker?.name,
          address: broker?.address || {
            postalZone: "",
            countrySubentityCode: "",
            countrySubentityName: "",
            cityCode: "",
            countryIdentificationCode: "",
            cityName: "",
            countryName: "",
            languaje: "",
            line: "",
          },
        }
  );

  const handleFinalFormSubmit = (values: any) => {
    if (
      values.companyID !== companyForm.companyID ||
      values.digitCheck !== companyForm.digitCheck ||
      values.registrationName !== companyForm.registrationName ||
      values.name !== companyForm.name ||
      values.address !== companyForm.address ||
      values.contact !== companyForm.contact ||
      values.sellerId !== companyForm.sellerId
    ) {
      companyForm.companyID = values.companyID.trim();
      companyForm.digitCheck = values.digitCheck.trim();
      companyForm.registrationName = values.registrationName.trim();
      companyForm.name = values.name.trim();
      companyForm.sellerId = values.sellerId;
      companyForm.address = values.address;
      companyForm.contact = values.contact;
      if (id !== "null") updateBroker(companyForm);
      else newBroker(companyForm);
    } else toast.error("No ha hecho ningún cambio");
  };

  const getCitieshandle = (idDepartamento: any) => {
    getCities(idDepartamento);
  };

  if (id !== "null" && !loadBroker)
    return <LoadingComponent content="Cargando" />;

  return (
    <div className="tabContentStyle">
      <FinalForm
        validate={validate}
        initialValues={companyForm}
        onSubmit={handleFinalFormSubmit}
        render={({ handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <Container textAlign="justified">
              <label>Nombre o Razón Social del emisor</label>
              <Field
                name="registrationName"
                value={companyForm.registrationName}
                component={TextInput}
              />
              <label>Nombre Comercial</label>
              <Field
                name="name"
                value={companyForm.name}
                component={TextInput}
              />
              <label>NIT del emisor</label>
              <Field
                name="companyID"
                value={companyForm.companyID}
                component={TextInput}
              />
              <label>DV del NIT del emisor</label>
              <Field
                name="digitCheck"
                value={companyForm.digitCheck}
                component={TextInput}
              />
              <label>Dirección</label>
              <Field
                name="address.line"
                value={companyForm.address?.line}
                component={TextInput}
              />
              <label>Email</label>
              <Field
                name="contact.email"
                value={companyForm.contact.email}
                component={TextInput}
              />
              <label>Teléfono</label>
              <Field
                name="contact.phone"
                value={companyForm.contact.phone}
                component={TextInput}
              />
              <label>Tipo de identificador fiscal</label>
              <p className="readonlyfield">31-NIT</p>
              <label>Código dentificador del país</label>
              <p className="readonlyfield">CO-Colombia</p>
              <label>Departamento</label>
              <Field name="address.countrySubentityCode">
                {(props) => (
                  <Form.Field error={props.meta.touched && !!props.meta.error}>
                    <Dropdown
                      name={props.input.name}
                      fluid
                      selection
                      search={true}
                      onChange={(e, data) => (
                        props.input.onChange(data.value),
                        getCitieshandle(data.value)
                      )}
                      defaultValue={companyForm.address?.countrySubentityCode}
                      options={countrySubentities}
                    />
                    {props.meta.touched && props.meta.error && (
                      <Label basic color="red">
                        {props.meta.error}
                      </Label>
                    )}
                  </Form.Field>
                )}
              </Field>
              <label>Ciudad</label>
              <Field name="address.cityCode">
                {(props) => (
                  <Form.Field error={props.meta.touched && !!props.meta.error}>
                    <Dropdown
                      name={props.input.name}
                      fluid
                      selection
                      search={true}
                      onChange={(e, data) => props.input.onChange(data.value)}
                      defaultValue={companyForm.address?.cityCode}
                      options={cities}
                    />
                    {props.meta.touched && props.meta.error && (
                      <Label basic color="red">
                        {props.meta.error}
                      </Label>
                    )}
                  </Form.Field>
                )}
              </Field>
              <label>Código postal</label>
              <Field
                name="address.postalZone"
                value={companyForm.address?.postalZone}
                component={TextInput}
              />
              <label>Vendedor</label>
              <Field
                name="sellerId"
                component={SelectInput}
                placeholder="Seleccionar vendedor"
                defaultValue={companyForm.sellerId}
                options={sellersOption}
              />
            </Container>
            <br />
            <Button
              loading={btEditBroker}
              disabled={btEditBroker}
              className="ui primary button"
              color="blue"
              type="submit"
              content="Guardar"
            />
          </Form>
        )}
      />
    </div>
  );
};

export default observer(EditBrokerForm);
